import { useEffect, useState } from 'react';
import { Outlet, useNavigate, useLocation } from 'react-router-dom';
import {
  Frame,
  Navigation,
  TopBar,
  Text,
  BlockStack,
  Banner,
  Spinner,
  InlineStack,
} from '@shopify/polaris';
import {
  HomeIcon,
  StoreIcon,
  SettingsIcon,
  ChartVerticalIcon,
  LockIcon,
  DiscountIcon,
  PersonIcon,
} from '@shopify/polaris-icons';
import { useApi } from '../hooks/useApi';

interface AdminPermissions {
  canViewDashboard: boolean;
  canViewShops: boolean;
  canManageShops: boolean;
  canViewUsers: boolean;
  canViewRevenue: boolean;
  canManagePricing: boolean;
  canManageAdmins: boolean;
}

interface AdminUser {
  id: number;
  email: string;
  name: string;
  picture: string | null;
  isOwner: boolean;
  permissions: AdminPermissions;
}

export default function AdminLayout() {
  const navigate = useNavigate();
  const location = useLocation();
  const { fetchApi } = useApi();
  const [user, setUser] = useState<AdminUser | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    checkAccess();
  }, []);

  async function checkAccess() {
    try {
      // Get current admin info and permissions
      const response = await fetchApi<{
        success: boolean;
        admin?: AdminUser;
        error?: string;
      }>('/super-admin/me');

      if (!response?.success || !response.admin) {
        setError(response?.error || 'Access denied');
        setLoading(false);
        return;
      }

      setUser(response.admin);
      setLoading(false);
    } catch (err) {
      setError('Failed to verify access');
      setLoading(false);
    }
  }

  if (loading) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
        <BlockStack gap="400" inlineAlign="center">
          <Spinner size="large" />
          <Text as="p">Verifying admin access...</Text>
        </BlockStack>
      </div>
    );
  }

  if (error) {
    return (
      <div style={{ padding: '2rem', maxWidth: '600px', margin: '0 auto' }}>
        <BlockStack gap="400">
          <Banner tone="critical" title="Access Denied">
            <p>{error}</p>
            <p>Super admin access is required to view this page.</p>
          </Banner>
          <button
            onClick={() => window.location.href = 'https://app.whenlyapp.com'}
            style={{
              padding: '0.75rem 1.5rem',
              background: '#008060',
              color: 'white',
              border: 'none',
              borderRadius: '4px',
              cursor: 'pointer',
            }}
          >
            Go to Main App
          </button>
        </BlockStack>
      </div>
    );
  }

  // Build navigation items based on permissions
  const navigationItems = [];

  if (user?.permissions.canViewDashboard) {
    navigationItems.push({
      label: 'Dashboard',
      icon: HomeIcon,
      onClick: () => navigate('/admin'),
      selected: location.pathname === '/admin',
    });
  }

  if (user?.permissions.canViewShops) {
    navigationItems.push({
      label: 'Customers',
      icon: StoreIcon,
      onClick: () => navigate('/admin/customers'),
      selected: location.pathname.startsWith('/admin/customers'),
    });
  }

  if (user?.permissions.canViewUsers) {
    navigationItems.push({
      label: 'Users',
      icon: PersonIcon,
      onClick: () => navigate('/admin/users'),
      selected: location.pathname.startsWith('/admin/users'),
    });
  }

  // Plans & Pricing - show if can view or manage pricing
  if (user?.permissions.canManagePricing || user?.isOwner) {
    navigationItems.push({
      label: 'Plans & Pricing',
      icon: SettingsIcon,
      onClick: () => navigate('/admin/plans'),
      selected: location.pathname === '/admin/plans',
    });

    navigationItems.push({
      label: 'Discount Codes',
      icon: DiscountIcon,
      onClick: () => navigate('/admin/discount-codes'),
      selected: location.pathname === '/admin/discount-codes',
    });
  }

  if (user?.permissions.canViewRevenue) {
    navigationItems.push({
      label: 'Revenue',
      icon: ChartVerticalIcon,
      onClick: () => navigate('/admin/revenue'),
      selected: location.pathname === '/admin/revenue',
    });
  }

  // Super Admins - only owner can manage, but all admins can view
  navigationItems.push({
    label: 'Super Admins',
    icon: LockIcon,
    onClick: () => navigate('/admin/admins'),
    selected: location.pathname === '/admin/admins',
  });

  const navigationMarkup = (
    <Navigation location={location.pathname}>
      <Navigation.Section
        title="Super Admin"
        items={navigationItems}
      />
    </Navigation>
  );

  const userMenuMarkup = user && (
    <TopBar.UserMenu
      name={user.name}
      initials={user.name.split(' ').map(n => n[0]).join('').toUpperCase()}
      detail={user.isOwner ? 'Owner' : 'Super Admin'}
      actions={[
        {
          items: [
            {
              content: 'Back to Main App',
              onAction: () => window.location.href = 'https://app.whenlyapp.com',
            },
            {
              content: 'Log out',
              onAction: async () => {
                await fetchApi('/auth/logout', { method: 'POST' });
                window.location.href = 'https://app.whenlyapp.com/login';
              },
            },
          ],
        },
      ]}
      avatar={user.picture || undefined}
      open={false}
      onToggle={() => {}}
    />
  );

  const topBarMarkup = (
    <TopBar
      showNavigationToggle
      userMenu={userMenuMarkup}
    />
  );

  const logo = {
    width: 124,
    topBarSource: '',
    accessibilityLabel: 'Whenly Admin',
  };

  return (
    <Frame
      logo={logo}
      topBar={topBarMarkup}
      navigation={navigationMarkup}
    >
      <div style={{ padding: '1rem' }}>
        <InlineStack gap="200" blockAlign="center">
          <Text as="span" variant="headingLg" fontWeight="bold">Whenly</Text>
          <Text as="span" tone="subdued">Super Admin</Text>
          {user?.isOwner && (
            <span style={{
              background: '#e3f1df',
              color: '#1a7f37',
              padding: '2px 8px',
              borderRadius: '4px',
              fontSize: '12px',
              fontWeight: 500,
            }}>
              Owner
            </span>
          )}
        </InlineStack>
      </div>
      <Outlet />
    </Frame>
  );
}
